/**
 * BOM de combo en ficha de producto / formulario (ERP: composición en el ítem).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { formatIsoDate } from '@shared/vueltoDateTime';
import { colors } from '../../shared/theme/colors';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { spacing } from '../../shared/theme/spacing';
import { typography } from '../../shared/theme/typography';
import { matchesSearchText } from '../../shared/format/searchText';
import { MIN_PRODUCT_SEARCH_QUERY_CHARS } from '../../shared/product-search/productSearchUx';
import { showInfo, showSuccess } from '../../shared/ui/appToast';
import { InventoryIsoDateField } from '../../shared/ui/InventoryIsoDateField';
import { OperationalConfirmModal } from '../../shared/modals/OperationalConfirmModal';
import { supabase } from '../../shared/supabase/supabaseClient';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import {
  fetchProductCombos,
  upsertProductCombo,
} from '../infra/productCombosRepo';
import {
  parseComboDateInput,
  validateComboDraft,
  type ProductCombo,
} from '../domain/productCombos';
import { invalidateComboParentProductIdsCache } from '../infra/comboParentProductIds';
import { fetchCatalogComboMetrics } from '../infra/comboMetricsRepo';
import { useSessionStore } from '../../shell/session/store/useSessionStore';

type ProductPick = {
  id: string;
  name: string;
  sku?: string | null;
  barcode?: string | null;
};

type ComponentDraft = {
  key: string;
  productId: string;
  qty: string;
};

function newComponentKey() {
  return `cmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function comboDayInput(ts: string): string {
  return formatIsoDate(new Date(ts));
}

type Props = {
  productId: string;
  productName: string;
  canEdit: boolean;
  /** Ficha = consulta; formulario = editar BOM (default). */
  presentation?: 'detail' | 'edit';
  /** Precio de venta del padre (margen en BOM). */
  salePrice?: number;
  formatMoney?: (value: number) => string;
  /** Tras guardar / desarmar combo — refrescar lista y flags del padre. */
  onSaved?: () => void;
};

export function CatalogProductComboPanel({
  productId,
  productName,
  canEdit,
  presentation = 'edit',
  salePrice = 0,
  formatMoney,
  onSaved,
}: Props) {
  const editable = canEdit && presentation === 'edit';
  const { entitlements } = usePlanEntitlements();
  const orgId = entitlements?.organization_id ?? null;
  const branchId = useSessionStore((s) => s.currentBranchId);

  const [loading, setLoading] = useState(true);
  const [referenceUnitCost, setReferenceUnitCost] = useState<number | null>(null);
  const [referenceComplete, setReferenceComplete] = useState(true);
  const [sellableQty, setSellableQty] = useState<number | null>(null);
  const [products, setProducts] = useState<ProductPick[]>([]);
  const [existingCombo, setExistingCombo] = useState<ProductCombo | null>(null);
  const [componentDrafts, setComponentDrafts] = useState<ComponentDraft[]>([]);
  const [componentFilter, setComponentFilter] = useState('');
  const [validFromInput, setValidFromInput] = useState('');
  const [validUntilInput, setValidUntilInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [uncomboConfirmOpen, setUncomboConfirmOpen] = useState(false);

  const refreshMetrics = useCallback(async () => {
    if (!branchId || !productId) {
      setReferenceUnitCost(null);
      setSellableQty(null);
      return;
    }
    const map = await fetchCatalogComboMetrics(branchId, [productId]);
    const m = map[productId];
    if (!m) {
      setReferenceUnitCost(null);
      setSellableQty(null);
      setReferenceComplete(true);
      return;
    }
    setReferenceUnitCost(m.referenceUnitCost > 0 ? m.referenceUnitCost : null);
    setReferenceComplete(m.referenceComplete);
    setSellableQty(m.sellableQty);
  }, [branchId, productId]);

  const load = useCallback(async () => {
    if (!orgId || !productId) {
      setProducts([]);
      setExistingCombo(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const prodRes = await supabase
        .from('products')
        .select('id, name, sku, barcode')
        .eq('organization_id', orgId)
        .eq('is_active', true)
        .or('is_misc.is.null,is_misc.eq.false')
        .is('deleted_at', null)
        .order('name')
        .limit(500);
      if (prodRes.error) throw new Error(prodRes.error.message);
      const list = (prodRes.data ?? []) as ProductPick[];
      setProducts(list);
      const map = await fetchProductCombos([productId]);
      const combo = map.get(productId) ?? null;
      setExistingCombo(combo);
      setComponentDrafts(
        (combo?.components ?? []).map((c) => ({
          key: newComponentKey(),
          productId: c.productId,
          qty: String(c.qty),
        })),
      );
      setValidFromInput(combo?.validFrom ? comboDayInput(combo.validFrom) : '');
      setValidUntilInput(combo?.validUntil ? comboDayInput(combo.validUntil) : '');
      setComponentFilter('');
      await refreshMetrics();
    } catch (e) {
      presentUnexpectedFailure({
        module: 'catalog',
        op: 'combo_panel.load',
        error: e,
        userMessage: 'No se pudo cargar la composición.',
      });
    } finally {
      setLoading(false);
    }
  }, [orgId, productId, refreshMetrics]);

  useEffect(() => {
    void load();
  }, [load]);

  const productNameCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of products) counts.set(p.name, (counts.get(p.name) ?? 0) + 1);
    return counts;
  }, [products]);

  const productLabel = useCallback(
    (p: ProductPick): string => {
      if ((productNameCounts.get(p.name) ?? 0) <= 1) return p.name;
      const code = p.sku?.trim() || p.barcode?.trim();
      if (code) return `${p.name} · ${code}`;
      return `${p.name} · #${p.id.slice(-4)}`;
    },
    [productNameCounts],
  );

  const resolveName = useCallback(
    (id: string) => {
      const p = products.find((prod) => prod.id === id);
      return p ? productLabel(p) : 'Producto';
    },
    [products, productLabel],
  );

  const componentCandidates = useMemo(() => {
    const q = componentFilter.trim();
    if (q.length < MIN_PRODUCT_SEARCH_QUERY_CHARS) return [];
    const used = new Set(componentDrafts.map((d) => d.productId));
    return products.filter(
      (p) =>
        p.id !== productId &&
        !used.has(p.id) &&
        (matchesSearchText(p.name, q) ||
          (p.sku?.trim() && matchesSearchText(p.sku, q)) ||
          (p.barcode?.trim() && matchesSearchText(p.barcode, q))),
    );
  }, [products, componentDrafts, componentFilter, productId]);

  const addComponent = (id: string) => {
    setComponentDrafts((prev) => [
      ...prev,
      { key: newComponentKey(), productId: id, qty: '1' },
    ]);
  };

  const updateComponentQty = (key: string, qty: string) => {
    setComponentDrafts((prev) =>
      prev.map((d) => (d.key === key ? { ...d, qty } : d)),
    );
  };

  const removeComponent = (key: string) => {
    setComponentDrafts((prev) => prev.filter((d) => d.key !== key));
  };

  const persist = useCallback(
    async (components: { productId: string; qty: number; sortOrder: number }[], validity: {
      validFrom: string | null;
      validUntil: string | null;
    }) => {
      await upsertProductCombo(productId, components, validity);
      invalidateComboParentProductIdsCache();
    },
    [productId],
  );

  const save = useCallback(async () => {
    if (saving || !editable) return;
    const components = componentDrafts
      .filter((d) => d.productId)
      .map((d, i) => ({
        productId: d.productId,
        qty: Number(d.qty) || 0,
        sortOrder: i + 1,
      }));
    const error = validateComboDraft(productId, components);
    if (error) {
      const messages: Record<string, string> = {
        combo_component_required: 'Elegí un producto para cada componente.',
        combo_cannot_contain_itself: 'El combo no puede contenerse a sí mismo.',
        combo_component_duplicated: 'Hay componentes repetidos.',
        combo_component_qty_invalid: 'La cantidad tiene que ser mayor a 0.',
      };
      showInfo(messages[error] ?? 'Revisá los componentes del combo.');
      return;
    }
    const validFrom = parseComboDateInput(validFromInput, false);
    const validUntil = parseComboDateInput(validUntilInput, true);
    if (validFrom === 'invalid' || validUntil === 'invalid') {
      showInfo('Vigencia', 'Revisá las fechas Desde/Hasta.');
      return;
    }
    if (validFrom && validUntil && new Date(validUntil) < new Date(validFrom)) {
      showInfo('Vigencia', '«Hasta» no puede ser anterior a «Desde».');
      return;
    }
    setSaving(true);
    try {
      await persist(components, { validFrom, validUntil });
      if (validFrom || validUntil) {
        const persisted = await fetchProductCombos([productId]);
        const saved = persisted.get(productId);
        const sameTs = (a: string | null, b: string | null) => {
          if (!a && !b) return true;
          if (!a || !b) return false;
          return new Date(a).getTime() === new Date(b).getTime();
        };
        const validityPersisted =
          components.length > 0 &&
          !!saved &&
          sameTs(saved.validFrom, validFrom) &&
          sameTs(saved.validUntil, validUntil);
        if (validityPersisted) {
          showSuccess('Combo guardado.');
        } else if (components.length > 0) {
          showInfo(
            'Combo guardado',
            'La vigencia no quedó guardada en el servidor. Revisala antes de vender.',
          );
        } else {
          showSuccess('El producto dejó de ser combo.');
        }
      } else {
        showSuccess(
          components.length ? 'Combo guardado.' : 'El producto dejó de ser combo.',
        );
      }
      setUncomboConfirmOpen(false);
      await load();
      await refreshMetrics();
      onSaved?.();
    } catch (e) {
      presentUnexpectedFailure({
        module: 'catalog',
        op: 'combo_panel.save',
        error: e,
        userMessage: 'No se pudo guardar el combo.',
      });
    } finally {
      setSaving(false);
    }
  }, [
    saving,
    editable,
    componentDrafts,
    validFromInput,
    validUntilInput,
    productId,
    persist,
    load,
    refreshMetrics,
    onSaved,
  ]);

  const uncombo = useCallback(async () => {
    if (saving || !editable) return;
    setSaving(true);
    try {
      await persist([], { validFrom: null, validUntil: null });
      showSuccess('El producto dejó de ser combo.');
      setUncomboConfirmOpen(false);
      await load();
      await refreshMetrics();
      onSaved?.();
    } catch (e) {
      presentUnexpectedFailure({
        module: 'catalog',
        op: 'combo_panel.uncombo',
        error: e,
        userMessage: 'No se pudo quitar el combo.',
      });
    } finally {
      setSaving(false);
    }
  }, [saving, editable, persist, load, refreshMetrics, onSaved]);

  if (loading) {
    return (
      <View style={styles.loadingWrap}>
        <ActivityIndicator color={colors.accent} />
      </View>
    );
  }

  const hasCombo = existingCombo != null && existingCombo.components.length > 0;
  const fmt = formatMoney ?? ((n: number) => String(Math.round(n)));
  const marginPct =
    referenceUnitCost != null &&
    referenceUnitCost > 0 &&
    salePrice > 0
      ? ((salePrice - referenceUnitCost) / referenceUnitCost) * 100
      : null;

  const referenceCostLabel =
    referenceUnitCost != null && referenceUnitCost > 0
      ? fmt(referenceUnitCost)
      : referenceComplete
        ? 'Sin costo en partes'
        : 'Faltan costos en alguna parte';
  const referenceIsMoney =
    referenceUnitCost != null && referenceUnitCost > 0;

  const showCostBlock = Boolean(branchId && (componentDrafts.length > 0 || hasCombo));
  const showPartsBlock = componentDrafts.length > 0;

  return (
    <View style={styles.body} testID="catalog.combo-panel">
      {!branchId && !showPartsBlock ? (
        <Text style={styles.hint}>Elegí sucursal para ver costo y vendibles.</Text>
      ) : null}

      {(showCostBlock || showPartsBlock) && (
        <View style={styles.shell}>
          {showCostBlock ? (
            <View style={styles.costSection}>
              <View style={styles.costHeaderRow}>
                <View style={styles.costPrimary}>
                  <Text style={styles.metricsKicker}>Costo de composición</Text>
                  <Text
                    style={[styles.metricsHero, !referenceIsMoney && styles.metricsHeroMuted]}
                    testID="catalog.combo-panel.reference-cost"
                  >
                    {referenceCostLabel}
                  </Text>
                  <Text style={styles.metricsHeroLabel}>Ref. partes × costo prom.</Text>
                </View>
              </View>
              {(sellableQty != null || marginPct != null) && (
                <View style={styles.metricsStatsRow}>
                  {sellableQty != null ? (
                    <View style={styles.metricsStat}>
                      <Text style={styles.metricsStatValue}>~{sellableQty}</Text>
                      <Text style={styles.metricsStatLabel}>vendibles</Text>
                    </View>
                  ) : null}
                  {marginPct != null ? (
                    <View style={styles.metricsStat}>
                      <Text style={styles.metricsStatValue}>{marginPct.toFixed(1)}%</Text>
                      <Text style={styles.metricsStatLabel}>margen ref.</Text>
                    </View>
                  ) : null}
                </View>
              )}
            </View>
          ) : !branchId ? (
            <View style={styles.costSection}>
              <Text style={styles.hint}>Elegí sucursal para ver costo y vendibles.</Text>
            </View>
          ) : null}

          {showCostBlock && showPartsBlock ? <View style={styles.shellDivider} /> : null}

          {showPartsBlock ? (
            <View style={styles.partsSection}>
              <View style={styles.partsHeadingRow}>
                <Text style={styles.partsHeading}>Partes</Text>
                <Text style={styles.partsCount}>{componentDrafts.length}</Text>
              </View>
              {componentDrafts.map((draft, index) =>
                editable ? (
                  <View
                    key={draft.key}
                    style={[
                      styles.partRow,
                      index < componentDrafts.length - 1 && styles.partRowBorder,
                    ]}
                  >
                    <TextInput
                      style={styles.qtyInput}
                      value={draft.qty}
                      onChangeText={(v) => updateComponentQty(draft.key, v)}
                      keyboardType="decimal-pad"
                      editable
                      testID={`combo-qty-${draft.productId}`}
                    />
                    <Text style={styles.componentName} numberOfLines={2}>
                      {resolveName(draft.productId)}
                    </Text>
                    <TouchableOpacity
                      onPress={() => removeComponent(draft.key)}
                      style={styles.removeBtn}
                      testID={`combo-remove-${draft.productId}`}
                    >
                      <Ionicons name="trash-outline" size={16} color={ticketColors.bad} />
                    </TouchableOpacity>
                  </View>
                ) : (
                  <View
                    key={draft.key}
                    style={[
                      styles.partRow,
                      index < componentDrafts.length - 1 && styles.partRowBorder,
                    ]}
                    testID={`combo-line-${draft.productId}`}
                  >
                    <View style={styles.qtyBadge}>
                      <Text style={styles.qtyBadgeText} numberOfLines={1}>
                        {draft.qty.trim() || '—'}
                      </Text>
                    </View>
                    <Text style={styles.partName} numberOfLines={2}>
                      {resolveName(draft.productId)}
                    </Text>
                  </View>
                ),
              )}
            </View>
          ) : (
            <View style={styles.partsSection}>
              <Text style={styles.emptyText}>
                {presentation === 'detail'
                  ? 'Todavía no tiene partes.'
                  : 'Agregá partes o guardá vacío para venderlo solo.'}
              </Text>
            </View>
          )}
        </View>
      )}

      {!showCostBlock && !showPartsBlock && branchId ? (
        <Text style={styles.emptyText}>
          {presentation === 'detail'
            ? 'Todavía no tiene partes.'
            : 'Agregá partes o guardá vacío para venderlo solo.'}
        </Text>
      ) : null}

      {!editable && (validFromInput || validUntilInput) ? (
        <View style={styles.validityReadOnly}>
          <Text style={styles.sectionTitle}>Vigencia</Text>
          <Text style={styles.metricsLine}>
            {validFromInput ? `Desde ${validFromInput}` : 'Sin inicio'}
            {' · '}
            {validUntilInput ? `Hasta ${validUntilInput}` : 'Sin fin'}
          </Text>
        </View>
      ) : null}

      {editable ? (
        <>
          <TextInput
            style={[styles.search, styles.componentSearch]}
            placeholder="Buscar producto para agregar…"
            placeholderTextColor={colors.textMuted}
            value={componentFilter}
            onChangeText={setComponentFilter}
            testID="combo-component-search"
          />
          {componentFilter.trim().length > 0 &&
          componentFilter.trim().length < MIN_PRODUCT_SEARCH_QUERY_CHARS ? (
            <Text style={styles.hint}>
              Escribí al menos {MIN_PRODUCT_SEARCH_QUERY_CHARS} caracteres para ver productos.
            </Text>
          ) : null}
          <View>
            {componentCandidates.slice(0, 30).map((p) => (
              <TouchableOpacity
                key={p.id}
                style={styles.pickRow}
                onPress={() => addComponent(p.id)}
                testID={`combo-add-${p.id}`}
              >
                <Text style={styles.pickName}>{productLabel(p)}</Text>
                <Ionicons name="add" size={18} color={colors.accent} />
              </TouchableOpacity>
            ))}
          </View>
          {componentFilter.trim().length >= MIN_PRODUCT_SEARCH_QUERY_CHARS &&
          componentCandidates.length === 0 ? (
            <Text style={styles.hint}>Nada coincide. Probá otro nombre o código.</Text>
          ) : null}

          <Text style={styles.sectionTitle}>Vigencia (opcional)</Text>
          <View style={styles.validityRow}>
            <View style={styles.validityField}>
              <InventoryIsoDateField
                value={validFromInput}
                onChange={setValidFromInput}
                emptyLabel="Desde"
                ariaLabel="Vigencia desde"
                clearButtonLabel="Quitar desde"
                iosEmptyBehavior="openPicker"
                size="compact"
              />
            </View>
            <View style={styles.validityField}>
              <InventoryIsoDateField
                value={validUntilInput}
                onChange={setValidUntilInput}
                emptyLabel="Hasta"
                ariaLabel="Vigencia hasta"
                clearButtonLabel="Quitar hasta"
                iosEmptyBehavior="openPicker"
                size="compact"
              />
            </View>
          </View>
          <Text style={styles.hint}>
            Sin fechas = siempre. Fuera de vigencia no se vende.
          </Text>

          <TouchableOpacity
            style={[styles.saveBtn, saving && styles.saveBtnDisabled]}
            onPress={() => void save()}
            disabled={saving}
            testID="combo-save"
          >
            {saving ? (
              <ActivityIndicator color={colors.surface} />
            ) : (
              <Text style={styles.saveBtnLabel}>Guardar composición</Text>
            )}
          </TouchableOpacity>

          {hasCombo ? (
            <TouchableOpacity
              style={styles.uncomboBtn}
              onPress={() => setUncomboConfirmOpen(true)}
              disabled={saving}
              accessibilityRole="button"
              testID="combo-uncombo"
            >
              <Text style={styles.uncomboBtnText}>Dejar de ser combo</Text>
            </TouchableOpacity>
          ) : null}
        </>
      ) : null}

      {uncomboConfirmOpen ? (
        <OperationalConfirmModal
          visible
          presentation="overlay"
          title="Dejar de ser combo"
          entityName={productName}
          message="Se quitan todos los componentes y la vigencia. El producto se vende solo, con su propio stock."
          confirmLabel="Quitar combo"
          destructive
          loading={saving}
          onClose={() => setUncomboConfirmOpen(false)}
          onConfirm={() => void uncombo()}
        />
      ) : null}
    </View>
  );
}

const rowFlex = Platform.select({
  web: { display: 'flex' as const, flexDirection: 'row' as const },
  default: { flexDirection: 'row' as const },
});

const styles = StyleSheet.create({
  loadingWrap: { paddingVertical: spacing.xl, alignItems: 'center' },
  body: { width: '100%', gap: spacing.md },
  hint: { ...typography.caption, color: colors.textMuted },
  shell: {
    width: '100%',
    alignSelf: 'stretch',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    overflow: 'hidden',
  },
  shellDivider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: ticketColors.ruleSoft,
  },
  costSection: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    backgroundColor: ticketColors.brandSoft,
    gap: spacing.sm,
  },
  costHeaderRow: {
    ...rowFlex,
    alignItems: 'flex-start',
    width: '100%',
  },
  costPrimary: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  metricsKicker: {
    fontSize: 11,
    lineHeight: 14,
    fontFamily: ticketFonts.sans,
    fontWeight: '600',
    color: ticketColors.brandInk,
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  metricsHero: {
    fontFamily: ticketFonts.mono,
    fontSize: 28,
    lineHeight: 34,
    fontWeight: '700',
    color: ticketColors.ink,
    fontVariant: ['tabular-nums'],
    marginTop: 4,
  },
  metricsHeroMuted: {
    fontFamily: ticketFonts.sans,
    fontSize: 16,
    lineHeight: 22,
    fontWeight: '600',
    fontVariant: [],
  },
  metricsHeroLabel: {
    fontSize: 12,
    lineHeight: 16,
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink3,
    marginTop: 2,
  },
  metricsStatsRow: {
    ...rowFlex,
    width: '100%',
    gap: spacing.sm,
  },
  metricsStat: {
    flex: 1,
    minWidth: 0,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    borderRadius: 10,
    backgroundColor: ticketColors.paper,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.brandBorderSoft,
  },
  metricsStatValue: {
    fontFamily: ticketFonts.mono,
    fontSize: 16,
    lineHeight: 20,
    fontWeight: '700',
    color: ticketColors.ink,
    fontVariant: ['tabular-nums'],
  },
  metricsStatLabel: {
    fontSize: 11,
    lineHeight: 14,
    fontFamily: ticketFonts.sans,
    fontWeight: '500',
    color: ticketColors.ink3,
    marginTop: 2,
  },
  metricsLine: {
    ...typography.body,
    color: ticketColors.ink2,
  },
  partsSection: {
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.xs,
    paddingTop: spacing.sm,
    width: '100%',
  },
  partsHeadingRow: {
    ...rowFlex,
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.sm,
    width: '100%',
  },
  partsHeading: {
    fontSize: 11,
    lineHeight: 14,
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    color: ticketColors.ink3,
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  partsCount: {
    fontSize: 12,
    lineHeight: 16,
    fontFamily: ticketFonts.mono,
    fontWeight: '600',
    color: ticketColors.brandPrimary,
    fontVariant: ['tabular-nums'],
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: ticketColors.brandSoft,
    overflow: 'hidden',
  },
  partRow: {
    ...rowFlex,
    alignItems: 'center',
    width: '100%',
    minHeight: 52,
    paddingVertical: spacing.sm,
    gap: spacing.md,
  },
  partRowBorder: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  qtyBadge: {
    width: 44,
    height: 44,
    flexShrink: 0,
    borderRadius: 10,
    backgroundColor: ticketColors.tile,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  qtyBadgeText: {
    fontFamily: ticketFonts.mono,
    fontSize: 15,
    fontWeight: '700',
    color: ticketColors.brandPrimary,
    fontVariant: ['tabular-nums'],
    textAlign: 'center',
  },
  partName: {
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
    fontSize: 15,
    lineHeight: 21,
    fontFamily: ticketFonts.sans,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  sectionTitle: {
    ...typography.caption,
    color: colors.textMuted,
    marginTop: spacing.sm,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  qtyInput: {
    width: 56,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    paddingVertical: 6,
    textAlign: 'center',
    color: ticketColors.ink,
    backgroundColor: ticketColors.tile,
    ...typography.body,
    fontFamily: ticketFonts.mono,
    fontVariant: ['tabular-nums'],
  },
  componentName: {
    flex: 1,
    ...typography.body,
    fontFamily: ticketFonts.sans,
    fontWeight: '500',
    color: ticketColors.ink,
  },
  validityReadOnly: { gap: 4 },
  removeBtn: { padding: spacing.xs },
  emptyText: {
    ...typography.caption,
    color: colors.textMuted,
    paddingVertical: spacing.sm,
  },
  search: {
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    color: ticketColors.ink,
    ...typography.body,
  },
  componentSearch: { marginTop: spacing.sm, marginBottom: 0 },
  pickRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  pickName: { ...typography.body, color: ticketColors.ink, flex: 1 },
  validityRow: { flexDirection: 'row', gap: spacing.sm },
  validityField: { flex: 1, minWidth: 0 },
  saveBtn: {
    marginTop: spacing.md,
    backgroundColor: colors.accent,
    borderRadius: 10,
    paddingVertical: spacing.sm + 4,
    alignItems: 'center',
  },
  saveBtnDisabled: { opacity: 0.6 },
  saveBtnLabel: { ...typography.body, color: colors.surface, fontWeight: '600' },
  uncomboBtn: {
    marginTop: spacing.sm,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  uncomboBtnText: {
    ...typography.caption,
    color: ticketColors.bad,
    fontWeight: '600',
  },
});
